//@flow
import React from 'react';
import { Helmet } from 'react-helmet';
import {
  setDisplayName,
  compose,
  defaultProps,
  withProps,
} from 'recompose';
import reactCSS from 'reactcss';
import { Status } from '@ux/input';
import { getStyles, modifier } from '@ux/style-utils';
import color from 'color';
import {
  omit,
  equals,
  path,
} from 'ramda';
import { TypefaceStyle } from '@ux/typeface';
import { Curvature, gu2px } from '@ux/theme-utils';
import longhand from '@team-griffin/css-longhand';
import { headingXs, headingS, headingM, content } from '@ux/type-size';

export { Status };

export const Design = {
  STATIC: 'STATIC',
  STICKY: 'STICKY',
};

const roundAllBorders = (radius: Number) => longhand('border', {
  topLeftRadius: radius,
  topRightRadius: radius,
  bottomLeftRadius: radius,
  bottomRightRadius: radius,
});

const mixWithWhite = (toMix) => color('white')
  .mix(color(toMix), 0.25)
  .string();

export const stylesheet = ({
  theme,
}) => {

  const palette = theme.palette;
  const typography = theme.typography;

  return {
    default: {
      input: {
        ...headingXs(typography),
        ...longhand('font', {
          weight: 300,
          family: typography[TypefaceStyle.CONTENT],
        }),
        ...longhand('border', {
          width: 1,
          style: 'solid',
          color: palette.grey10,
        }),
        ...longhand('padding', {
          top: gu2px(2, theme.spacing),
          left: gu2px(3, theme.spacing),
          right: gu2px(5, theme.spacing),
          bottom: gu2px(2, theme.spacing),
        }),
        ...roundAllBorders(0),
        letterSpacing: 0.2,
        backgroundColor: '#fff',
        display: 'block',
        width: '100%',
        height: '100%',
        minHeight: 30,
        color: palette.grey03,
        WebkitAppearance: 'none', // due to mobile defaults adding shadow
        boxShadow: 'inset 0 0 0 2px #fff',
        transition: `
          color 0.2s ease-in-out,
          box-shadow 0.2s ease-in-out,
          border-color 0.2s ease-in-out,
          background-color 0.2s ease-in-out
        `,
      },
    },

    active: {
      input: {
        outline: 0,
        boxShadow: `inset 0 0 0 2px ${mixWithWhite(palette.primary)}`,
        borderColor: palette.primary,
      },
    },

    readOnly: {
      input: {
        backgroundColor: palette.grey11,
        color: palette.grey09,
      },
    },

    disabled: {
      input: {
        cursor: 'not-allowed',
        backgroundColor: palette.grey11,
        color: palette.grey09,
      },
    },

    [modifier('status', Status.ERROR)]: {
      input: {
        borderColor: palette.red01,
        boxShadow: `inset 0 0 0 2px ${mixWithWhite(palette.red01)}`,
      },
    },

    [modifier('curvature', Curvature.OFF)]: {
      input: {
        ...roundAllBorders(0),
      },
    },

    [modifier('curvature', Curvature.WEAK)]: {
      input: {
        ...roundAllBorders(3),
      },
    },

    [modifier('curvature', Curvature.STRONG)]: {
      input: {
        ...roundAllBorders(8),
      },
    },

    isMinSm: {
      input: {
        ...headingS(typography),
        ...longhand('padding', {
          top: gu2px(2, theme.spacing),
          left: gu2px(3, theme.spacing),
          right: gu2px(5, theme.spacing),
          bottom: gu2px(2, theme.spacing),
        }),
      },
    },

    isMinMd: {
      input: {
        ...headingM(typography),
        ...longhand('padding', {
          top: gu2px(2, theme.spacing),
          left: gu2px(3, theme.spacing),
          right: gu2px(5, theme.spacing),
          bottom: gu2px(2, theme.spacing),
        }),
      },
    },

    [modifier('design', Design.STICKY)]: {
      input: {
        ...content(typography),
        ...longhand('padding', {
          top: gu2px(1, theme.spacing),
          left: gu2px(2, theme.spacing),
          right: gu2px(5, theme.spacing),
          bottom: gu2px(1, theme.spacing),
        }),
      },
    },
  };
};

// eslint-disable-next-line no-shadow
export const stylesheetToStyles = (stylesheet, props) => reactCSS(stylesheet, {
  active: props.active,
  disabled: props.disabled,
  readOnly: props.readOnly,
  status: props.status,
  curvature: props.theme.curvature,
  isMinSm: props.isMinSm,
  isMinMd: props.isMinMd,
  design: props.design,
});

const createPsuedoStyles = ({
  theme,
}) => (
  <Helmet>
    <style>
      {`
.dst-search-input::-webkit-input-placeholder {
  letter-spacing: 0.2;
  color: ${theme.palette.grey09};
  transition: color 0.2s ease-in-out;
},
.dst-search-input:focus::-webkit-input-placeholder: {
  color: transparent;
}
      `}
    </style>
  </Helmet>
);

export const PureSearchInput = ({
  styles,
  theme,
  inputProps,
}) => (
  <span>
    {createPsuedoStyles({
      theme,
    })}
    <input
      style={styles.input}
      className="dst-search-input"
      {...inputProps}
    />
  </span>
);

export const enhance = compose(
  setDisplayName('SearchInput'),
  defaultProps({
    type: 'text',
    active: false,
    status: Status.DEFAULT,
    design: Design.STATIC,
  }),
  withProps((ownerProps) => ({
    styles: getStyles(stylesheet, stylesheetToStyles, ownerProps),
    inputProps: omit([
      'theme',
      'input',
      'meta',
      'status',
      'isMinSm',
      'isMinMd',
      'design',
      'active',
      'styles',
    ], ownerProps),
  })),
);

export const SearchInput = enhance(PureSearchInput);

export const ReduxFormSearchInputAdapter = ({
  theme,
  status,
  ...fieldProps
}) => (
  <SearchInput
    theme={theme}
    status={status}
    {...fieldProps}
    {...fieldProps.input}
    active={equals(true, path([ 'meta', 'active' ], fieldProps))}
  />
);

export default SearchInput;
